import { getCurrentLocale } from "../i18n";

// ============================================================
// DATE FORMATTERS
// ============================================================

/**
 * Formats an ISO 8601 date string to Polish locale date.
 * Example: "2024-06-15T10:00:00.000Z" → "15 cze 2024"
 */
export function formatDatePolish(isoDateString: string): string {
  try {
    return new Date(isoDateString).toLocaleDateString(getCurrentLocale(), {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return isoDateString;
  }
}

/**
 * Generates a Google Maps URL for a single location or a route.
 */
export function getGoogleMapsUrl(
  start?: { lat?: number; lng?: number },
  end?: { lat?: number; lng?: number },
): string {
  if (start?.lat && start?.lng && end?.lat && end?.lng) {
    return `https://www.google.com/maps/dir/?api=1&origin=${start.lat},${start.lng}&destination=${end.lat},${end.lng}`;
  }
  if (start?.lat && start?.lng) {
    return `https://www.google.com/maps/search/?api=1&query=${start.lat},${start.lng}`;
  }
  return "";
}

export function getGoogleMapsRoutePlannerUrl(
  points: Array<{ lat?: number; lng?: number }>,
): string {
  const validPoints = points.filter(
    (point) => typeof point.lat === "number" && typeof point.lng === "number",
  );

  if (validPoints.length < 2) {
    return "";
  }

  const origin = `${validPoints[0].lat},${validPoints[0].lng}`;
  const destination = `${validPoints[validPoints.length - 1].lat},${validPoints[validPoints.length - 1].lng}`;
  const waypoints = validPoints
    .slice(1, -1)
    .map((point) => `${point.lat},${point.lng}`)
    .join("|");

  const url = new URL("https://www.google.com/maps/dir/");
  url.searchParams.set("api", "1");
  url.searchParams.set("origin", origin);
  url.searchParams.set("destination", destination);

  if (waypoints) {
    url.searchParams.set("waypoints", waypoints);
  }

  return url.toString();
}

// ============================================================
// ASSET URL RESOLVER
// ============================================================

const BACKEND_BASE_URL =
  import.meta.env.VITE_APP_BACKEND_URL ?? "http://127.0.0.1:1337";

/**
 * Resolves a Strapi asset URL.
 * If the URL is already absolute, returns it as-is.
 * If relative, prepends the backend base URL.
 */
export function resolveAssetUrl(
  relativeOrAbsoluteUrl?: string,
): string | undefined {
  if (!relativeOrAbsoluteUrl) return undefined;
  if (relativeOrAbsoluteUrl.startsWith("http")) return relativeOrAbsoluteUrl;
  return `${BACKEND_BASE_URL}${relativeOrAbsoluteUrl}`;
}

// ============================================================
// VALIDATION HELPERS
// ============================================================

/**
 * Basic Polish NIP (tax identification number) format validator.
 * NIP must be exactly 10 digits.
 */
export function isValidNip(nipValue: string): boolean {
  const digitsOnly = nipValue.replace(/[-\s]/g, "");
  if (!/^\d{10}$/.test(digitsOnly)) return false;

  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  const checkDigit = parseInt(digitsOnly[9], 10);
  const weightedSum = weights.reduce(
    (sum, weight, index) => sum + weight * parseInt(digitsOnly[index], 10),
    0,
  );
  return weightedSum % 11 === checkDigit;
}

/**
 * Basic email format validator.
 */
export function isValidEmail(emailAddress: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress);
}

/**
 * Checks whether a string contains a non-empty value after trimming.
 */
export function hasNonEmptyValue(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

/**
 * Basic password strength validator for user-facing forms.
 */
export function isValidPassword(passwordValue: string): boolean {
  const trimmed = passwordValue.trim();
  return (
    trimmed.length >= 8 &&
    /[0-9]/.test(trimmed) &&
    /[A-Z]/.test(trimmed) &&
    /[a-z]/.test(trimmed) &&
    /[^A-Za-z0-9]/.test(trimmed)
  );
}

/**
 * Polish phone number validator. Accepts formats like: +48 123 456 789, 123-456-789, 123456789
 */
export function isValidPolishPhoneNumber(phoneNumber: string): boolean {
  const digitsOnly = phoneNumber.replace(/[\s\-+]/g, "");
  return /^(\d{9}|48\d{9})$/.test(digitsOnly);
}

/**
 * REGON validator. Accepts 9 or 14 digits.
 */
export function isValidRegon(regonValue: string): boolean {
  const digitsOnly = regonValue.replace(/\s+/g, "");
  return /^\d{9}(\d{5})?$/.test(digitsOnly);
}

/**
 * Validates a website URL, allowing values without protocol.
 */
export function isValidWebsiteUrl(websiteValue: string): boolean {
  const trimmed = websiteValue.trim();
  if (!trimmed) return true;

  try {
    const normalized = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    const parsed = new URL(normalized);
    return Boolean(parsed.hostname) && parsed.hostname.includes(".");
  } catch {
    return false;
  }
}

/**
 * Validates a latitude string.
 */
export function isValidLatitude(latitudeValue: string): boolean {
  if (!latitudeValue.trim()) return true;
  const parsed = Number(latitudeValue);
  return Number.isFinite(parsed) && parsed >= -90 && parsed <= 90;
}

/**
 * Validates a longitude string.
 */
export function isValidLongitude(longitudeValue: string): boolean {
  if (!longitudeValue.trim()) return true;
  const parsed = Number(longitudeValue);
  return Number.isFinite(parsed) && parsed >= -180 && parsed <= 180;
}

/**
 * Checks whether both coordinates are provided together and remain valid.
 */
export function hasValidCoordinatePair(
  latitudeValue: string,
  longitudeValue: string,
): boolean {
  const hasLatitude = hasNonEmptyValue(latitudeValue);
  const hasLongitude = hasNonEmptyValue(longitudeValue);

  if (!hasLatitude && !hasLongitude) return true;
  if (hasLatitude !== hasLongitude) return false;

  return isValidLatitude(latitudeValue) && isValidLongitude(longitudeValue);
}

/**
 * Normalizes IBAN to the backend format: uppercase without spaces.
 */
export function normalizeIban(ibanValue: string): string {
  return ibanValue.replace(/\s+/g, "").toUpperCase();
}

/**
 * IBAN validator using the ISO 7064 mod-97 check.
 */
export function isValidIban(ibanValue: string): boolean {
  const normalized = normalizeIban(ibanValue);
  if (!normalized) return true;
  if (!/^[A-Z]{2}[0-9A-Z]{13,32}$/.test(normalized)) return false;

  const rearranged = `${normalized.slice(4)}${normalized.slice(0, 4)}`;
  let remainder = 0;

  for (const char of rearranged) {
    const replacement = /[A-Z]/.test(char)
      ? String(char.charCodeAt(0) - 55)
      : char;

    for (const digit of replacement) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }

  return remainder === 1;
}
